import { Icon, IconButton } from '@material-ui/core';
import { UseStore } from 'zustand';
import { UserProfile, getWorksheetTypes } from '../../api/user-profile-api';
import { Visit, summaryOwner, workflow } from '../../core';
import { AuditBatch, auditBatch } from '../../core/audit-batch-api';
import { auditWorksheetSearchApi } from '../../core/audit-worhsheet-search-api';
import { coderProductivity } from '../../core/coder-productivity';
import { formatLocalDate } from '../../core/date-time';
import { documentResolver } from '../../core/document-resolver';
import { personName } from '../../core/format';
import { OrganizationApi, OrganizationScope, organizationApi, organizationScope } from '../../core/organization-api';
import { PageApi, TableColumn, TableProps, createPageApi } from '../../core/paged-table';
import { history } from '../../core/router';
import { getStoredOrgUrlParams } from '../../core/url-params';
import { getCodingConfigRequest, hospitalistEnabled, setCurrentVisitSession } from '../../core/visit-context';
import {
  auditIdField,
  auditTypeField,
  chartOwnerLastField,
  locationField,
  mrnField,
  ownerLastField,
  patientLastField,
  serviceDateField,
  visitAccountField,
  visitDepartmentField,
  visitFirstDateInCodingField,
  visitInsuranceField,
  worksheetStateField,
  worksheetTypeField,
} from '../../core/visit-queue';
import { VisitSearchApi, visitSearchApi } from '../../core/visit-search-api';
import { currentWorkflow, isAuditReviewWorkflow, setDefaultActivity, workflows } from '../../core/workflow-api';

import { documentApi2 } from '../../core/document-viewer/document-api';
import { AccountLink, AgeInQueue, WorksheetIcon } from './coding-status';
import { resolveStateColumns } from './column-resolver';
import { FacilityWorksheetIcon } from './facility-status';

const params = getStoredOrgUrlParams();
documentApi2.setWorkflow(params.workflow);

function stateColumns(facility = false, inOut = false, col: TableColumn[] = null, showFlags = false): TableColumn[] {
  if (!facility) {
    return [
      {
        label: 'State',
        sort: true,
        render: (r: Visit) => WorksheetIcon({ ...r.wsSummaries[0], showFlags }),
        style: { width: 90 },
        data: worksheetStateField,
      },
    ];
  }

  if (col?.length) {
    return col;
  }
  const columns = [
    {
      label: 'Profee',
      sort: true,
      render: (r: Visit) => <FacilityWorksheetIcon {...r.wsSummaries.filter((v) => v.worksheetType === 'PHYSICIAN')[0]} visit={r} hideFlags={showFlags} />,
      style: { width: 90 },
      data: worksheetStateField,
    },
  ];

  if (inOut) {
    columns.push(
      {
        label: 'In Pt.',
        sort: true,
        render: (r: Visit) => <FacilityWorksheetIcon  {...r.wsSummaries.filter((v) => v.worksheetType === 'INPATIENT')[0]} visit={r} hideFlags={showFlags} />,
        style: { width: 80 },
        data: worksheetStateField,
      },

      {
        label: 'Out Pt.',
        sort: true,
        render: (r: Visit) => <FacilityWorksheetIcon  {...r.wsSummaries.filter((v) => v.worksheetType === 'OUTPATIENT')[0]} visit={r} hideFlags={showFlags} />,
        style: { width: 90 },
        data: worksheetStateField,
      }
    );
  }
  return columns;
}
export var visitColumns = (facility = false, inOut = false, col: TableColumn[] = null, workflow?: string, showFlags = false): TableColumn[] => {

  const columns: TableColumn[] = [
    {
      label: 'Age',
      sort: true,
      render: (r) => AgeInQueue(r.firstDateInCoding),
      style: { width: 40, padding: 'default', align: 'center' },
      data: visitFirstDateInCodingField,
    },
  ];

  columns.push(...stateColumns(facility, inOut, col, showFlags));
  columns.push(
    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: visitAccountField,
    },
    {
      label: 'MRN',
      id: 'mrn',
      sort: true,
      style: { width: 90 },
      data: mrnField,
    },
    {
      label: 'DOS',
      sort: true,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: serviceDateField,
    });

  if (showFlags) {

    columns.push(
      {
        label: 'Previous Workflow',
        sort: true,
        render: (r: Visit) => {
          const o = r.wsSummaries[0];
          if (!o) {
            return '';
          }
          return o.prevWorkflow || ''
        },
        style: { width: '12vw' },
        data: patientLastField,
      },
      {
        label: 'Previous Workflow Owner',
        sort: true,
        render: (r: Visit) => {
          const o = r.wsSummaries[0];
          return personName(o?.prevWorkflowOwner)
        },
        style: { width: '15vw' },
        data: patientLastField,
      });

  }


  columns.push(
    {
      label: 'Patient',
      sort: true,
      render: (r) => personName(r.patient),
      style: { width: '15vw' },
      data: patientLastField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.wsSummaries[0]?.owningProvider),
      style: { width: '15vw' },
      data: ownerLastField,
    },
    {
      label: 'Insurance',
      sort: true,
      render: (r) => (r.insurances && r.insurances[0]?.companyName) || '' || '',
      style: { width: '17vw' },
      data: visitInsuranceField,
    },
    {
      render: (row) => (
        <IconButton color="primary" onClick={() => documentResolver.openWindow({ ...row, workflow }, true)}>
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    {
      render: (row) => {
        return <AccountLink onClick={() => setCurrentVisitSession(row)} route="/tool" icon="play_circle_filled" />;
      },
      style: { width: 24, padding: 'none' },
    }
  );
  return columns;
};

export const reviewColumns = (facility = false, col: TableColumn[] = null): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      label: 'Age',
      sort: true,
      render: (r) => AgeInQueue(r.firstDateInCoding),
      style: { width: 40, padding: 'default', align: 'center' },
      data: visitFirstDateInCodingField,
    },
  ];

  columns.push(...stateColumns(facility, false, col,));
  columns.push(
    {
      label: 'Owner',
      sort: true,
      render: (r) => personName(r.owner),
      style: { width: 90 },
      data: chartOwnerLastField,
    },
    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: visitAccountField,
    },
    {
      label: 'MRN',
      id: 'mrn',
      sort: true,
      style: { width: 90 },
      data: mrnField,
    },
    {
      label: 'DOS',
      sort: true,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: serviceDateField,
    },
    {
      label: 'Patient',
      sort: true,
      render: (r) => personName(r.patient),
      style: { width: '15vw' },
      data: patientLastField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.wsSummaries[0]?.owningProvider),
      style: { width: '15vw' },
      data: ownerLastField,
    },
    {
      label: 'Insurance',
      sort: true,
      render: (r) => (r.insurances && r.insurances[0]?.companyName) || '' || '',
      style: { width: '17vw' },
      data: visitInsuranceField,
    },
    {
      render: (row) => (
        <IconButton color="primary" onClick={() => documentResolver.openWindow(row, true)}>
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    {
      render: (row) => {
        return <AccountLink onClick={() => setCurrentVisitSession(row)} route="/tool" icon="play_circle_filled" />;
      },
      style: { width: 24, padding: 'none' },
    }
  );
  return columns;
};

export const auditColumns = (col: TableColumn[] = null, activity: string, workflow: string, showFlags?: boolean): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      label: 'Age',
      sort: true,
      render: (r) => AgeInQueue(r.firstDateInCoding),
      style: { width: 40, padding: 'default', align: 'center' },
      data: visitFirstDateInCodingField,
    },
  ];

  columns.push(...stateColumns(false, false, col, showFlags));

  const writeAuditRuleName = (r) => {
    return (r?.auditInfo?.auditRules?.[0]?.auditRuleName || r?.auditId);
  };

  activity != 'CODEREVIEW' && columns.push(
    {
      label: 'AuditId',
      render: (r) => {
        return writeAuditRuleName(r.wsSummaries[0]);
      },
      style: { width: 150 },
      sort: true,
      data: auditIdField,
    },
    {
      label: 'AuditType',
      render: (r) => {
        return r.wsSummaries[0]?.auditType;
      },
      sort: true,
      style: { width: 90 },
      data: auditTypeField,
    });

  columns.push(
    {
      label: 'WorksheetType',
      render: (r) => {
        return r.wsSummaries[0]?.worksheetType;
      },
      sort: true,
      style: { width: 90 },
      data: worksheetTypeField,
    },
    {
      label: 'Location',
      id: 'locationCode',
      sort: true,
      style: { width: 90 },
      data: locationField,
    },
    {
      label: 'Department',
      id: 'departmentCode',
      render: (r) => {
        return r.wsSummaries[0]?.departmentCode;
      },
      sort: true,
      style: { width: 90 },
      data: visitDepartmentField,
    },
    {
      label: activity === 'CODEREVIEW' ? 'Reviewed' : 'Audited',
      sort: true,
      render: (r) => personName(summaryOwner(r, activity)),
      style: { width: 90 },
      data: chartOwnerLastField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.wsSummaries[0]?.owningProvider),
      style: { width: '150' },
      data: ownerLastField,
    },
    {
      label: 'MRN',
      id: 'mrn',
      sort: true,
      style: { width: 90 },
      data: mrnField,
    },

    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: visitAccountField,
    },
    {
      label: 'DOS',
      sort: true,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: serviceDateField,
    },
    {
      label: 'Patient',
      sort: true,
      render: (r) => personName(r.patient),
      style: { width: '15vw' },
      data: patientLastField,
    },
    {
      render: (row) => (
        <IconButton color="primary" onClick={() => documentResolver.openWindow({ ...row, workflow }, true)}>
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    {
      render: (row) => {
        return <AccountLink onClick={() => setCurrentVisitSession(row)} route="/tool" icon="play_circle_filled" />;
      },
      style: { width: 24, padding: 'none' },
    }
  );
  return columns;
};

//TODO: remove dependency on TableProps

export interface VisitListApi extends Omit<TableProps, 'rows'> {
  org: UseStore<OrganizationApi>;
  api: VisitSearchApi;
  sortApi: VisitSearchApi;
  pageApi: PageApi;
  workflow: string;
  showFlags?: boolean;
  getColumns: () => TableColumn[];
  resetPage: () => void;
  setUser: (a: UserProfile) => void;
  onLogout?: () => void;
}

let pendingWorflow = false;
export const setPendingWorkflow = (b: boolean) => (pendingWorflow = b);

export const pageApi = createPageApi();

export function visitListApiActivity(): VisitListApi {
  setDefaultActivity(params.activity);

  let faclilityColumns = false;

  const api = params.workflow === 'auditReviewStd' ? auditWorksheetSearchApi(pageApi) : visitSearchApi(pageApi);
  const sortApi = params.workflow === 'auditReviewStd' ? auditWorksheetSearchApi(pageApi) : visitSearchApi(pageApi);

  if (params.locationCode?.length) {
    faclilityColumns = hospitalistEnabled(params as any);
  }

  api.data.setState({ pendingNavigation: !!params.workflow });

  const resetPage = () => {
    const s = organizationApi.getState().selection;
    const pending = api.data.getState().pendingNavigation;
    const dept = s?.department?.code || params.departmentCode;

    if (!pending && dept && s?.location) {
      api.actions.setQuery({
        pageNumber: 0,
        locationCode: s.location.code,
        departmentCode: dept,
      });
      pageApi.setState({ pageNumber: 0 });
      coderProductivity.getState().update();
    }
  };

  let currentScope: OrganizationScope;

  const loadCodingConfig = async (s?: OrganizationScope) => {
    s = s || currentScope;

    if (!s && params.locationCode) {
      s = params as OrganizationScope;
    }

    if (!s) {
      return;
    }

    currentScope = s;

    const workflow = params.workflow;
    //TODO: a lighter weight provider request
    if (workflow) {
      const response = await getCodingConfigRequest({
        location: s.locationCode,
        department: s.departmentCode || 'ED',
        worksheetTypes: ['PHYSICIAN'],
        workflowNames: [workflow],
      });

      try {
        if (response?.result) {
          const { providers, workflowStateMap } = response.result;
          if (providers?.length) {
            api.data.setState({ providers });
          }

          if (workflowStateMap) {
            const states = workflowStateMap[workflow];

            if (states?.length) {
              api.data.setState({ stateFilters: states });
            }
          }
        }
      } catch (err) {
        console.log('Coding config loading error', err);
      }
    }
  };

  auditBatch.events.batch((a: AuditBatch) => {
    api.actions.setQuery({ auditId: a?.auditId || undefined });
    api.actions.search();
  });

  let inOut = params?.worksheetTypes?.length > 1;

  organizationScope.subscribe(async (s) => {
    const workflow = params.workflow || currentWorkflow();

    if (!pendingWorflow && workflow && params.activity) {
      pendingWorflow = true;
      await workflows.actions.load({
        location: s.locationCode,
        department: s.departmentCode || params.departmentCode || 'ED',
        workflow: workflow as any,
        activity: params.activity,
      });
      pendingWorflow = false;

      if (params.activity === 'AUDIT') {
        auditBatch.actions.clearBatch({
          locationCode: s.locationCode,
          departmentCode: s.departmentCode,
        });
      }

      faclilityColumns = hospitalistEnabled(s);

      api.actions.setQuery({
        locationCode: s.locationCode,
        departmentCode: s.departmentCode || 'ED',
        activity: params.activity
      });

      loadCodingConfig(s);
      updateWorksheetTypes();
    }
  });

  function resetSearch() {
    resetPage();
    api.actions.reset();
    api.actions.search();
  }
  workflows.events.workflowChanged((a: string) => {
    if (!api.data.getState().pendingNavigation) {
      resetSearch();
    } else {
      api.data.setState({ pendingNavigation: false });
      resetSearch();
    }

    loadCodingConfig();

    if (a === 'auditStd') {
      const s = organizationApi.getState().selection;
      if (s?.department && s?.location) {
        auditBatch.actions.clearBatch({
          locationCode: s.location.code,
          departmentCode: s.department.code,
        });
      }
    }
  });

  const showFlags = params.activity === 'CODEREVIEW';
  let stateColumns: TableColumn[];

  function updateWorksheetTypes() {
    const s = organizationApi.getState().selection; s
    if (user && s?.organization && s?.location) {
      const worksheetTypes = getWorksheetTypes(user, params.activity || 'CODING', s.organization.code, [s.location.code]);
      stateColumns = resolveStateColumns(worksheetTypes, showFlags);
    }
  }
  let user: UserProfile;
  function setUser(a: UserProfile) {
    user = a;
    updateWorksheetTypes();
  }

  function getColumns() {
    return isAuditReviewWorkflow() ? auditColumns(stateColumns, params.activity, params.workflow, showFlags) : visitColumns(faclilityColumns, inOut, stateColumns, params.workflow, showFlags);
  }

  function onLogout() {
    history.push('/logout');
  }


  return {
    sortApi,
    showFlags,
    resetPage,
    api,
    getColumns,
    columns: visitColumns(false, false, null, null, showFlags),
    org: organizationApi,
    pageApi,
    onLogout,
    setUser,
    workflow: params.workflow,
  };
}
